import { defaultMarkdownParser } from 'prosemirror-markdown';
import { DOMSerializer } from 'prosemirror-model';

/**
 * Markdown -> HTML for finished AI answers (achievable E3).
 *
 * Reuses the prosemirror stack the app already ships (the lesson-note editor
 * imports markdown the same way — see pmMarkdownImport.ts) instead of adding a
 * markdown dependency: markdown-it exists only as a transitive dep and pnpm's
 * strict layout forbids importing it directly.
 *
 * Safety: defaultMarkdownParser runs markdown-it in "commonmark" mode with
 * html:false (raw HTML is treated as text), and DOMSerializer builds nodes via
 * createElement/textContent — so the output contains no attacker-controlled
 * markup. markdown-it's validateLink additionally rejects javascript: URLs.
 */

let cachedSerializer: DOMSerializer | null = null;

function escapeFallback(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return `<p>${div.innerHTML.replace(/\n/g, '<br>')}</p>`;
}

export function renderAiMarkdown(markdown: string): string {
  const text = markdown ?? '';
  if (!text.trim()) return '';
  try {
    const doc = defaultMarkdownParser.parse(text);
    if (!doc) return escapeFallback(text);
    cachedSerializer ??= DOMSerializer.fromSchema(defaultMarkdownParser.schema);
    const fragment = cachedSerializer.serializeFragment(doc.content, { document });
    const container = document.createElement('div');
    container.appendChild(fragment);
    return container.innerHTML;
  } catch {
    return escapeFallback(text);
  }
}
